/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

import { ConformityType, ConformityTypeMessages } from "@/enums/ConformityType";
import { filterObject, filterObjectExcluding } from "@/utils/filterObject";
import { recoveryRoom as recoveryRoomInitialValues } from "@/utils/initialValues/recoveryRoom";
import { useApi } from "@/hooks/useApi";
import { useAttendanceId } from "@/hooks/useAttendanceId";
import { useSwal } from "@/hooks/useSwal";

import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { FormRadio } from "@/components/Radio/FormRadio";
import { ProfessionalWithRolesSelect } from "@/components/Selects/ProfessionalWithRolesSelect";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { SaveButton } from "@/components/Buttons/SaveButton";

import "./styles.scss";

type RecoveryRoom = typeof recoveryRoomInitialValues;

type Props = {
	remainingTime: number;
	surgicalAttendance: SurgicalAttendance;
};

export function RecoveryRoomForm({ remainingTime, surgicalAttendance }: Props) {
	const firstRender = useRef(true);
	const alreadyStored = useRef(false);
	const [isLoading, setIsLoading] = useState(true);

	const { api } = useApi();
	const { attendanceId } = useAttendanceId();
	const { Toast } = useSwal();

	const expertise = surgicalAttendance?.expertise;
	const isPregnant = expertise?.pregnant;

	async function sendData(values: RecoveryRoom) {
		const method = alreadyStored.current ? "put" : "post";

		const filteredValues = filterObjectExcluding(values, ["arrival_binomial"]) as RecoveryRoom;

		const { data } = await api[method](
			`/surgical-center/${attendanceId}/recovery-room`,
			isPregnant ? values : filteredValues
		);

		return data;
	}

	const formik = useFormik({
		initialValues: recoveryRoomInitialValues,
		async onSubmit(values) {
			try {
				await sendData(values);

				Toast.fire({
					icon: "success",
					title: `Dados da Sala de Recuperação ${
						alreadyStored ? "atualizados" : "salvos"
					} com sucesso!`,
				});

				alreadyStored.current = true;
			} catch (error) {
				console.error(error);
				Toast.fire({
					icon: "error",
					title: "Erro ao salvar dados da sala de recuperação!",
				});
			}
		},
	});

	async function handleSubmit() {
		try {
			await sendData(formik.values);

			alreadyStored.current = true;
		} catch (error) {
			console.log(error);
		}
	}

	async function fetchRecoveryRoomData() {
		try {
			const { data } = await api.get(`/surgical-center/${attendanceId}/recovery-room`);

			const recoveryRoomInitialValuesKeys = Object.keys(recoveryRoomInitialValues);
			const filteredRecoveryRoom = filterObject(data, recoveryRoomInitialValuesKeys);

			formik.setValues({
				...formik.values,
				...filteredRecoveryRoom,
			});

			alreadyStored.current = true;
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	}

	useEffect(() => {
		fetchRecoveryRoomData();
	}, []);

	useEffect(() => {
		if (!alreadyStored.current && !isLoading) {
			handleSubmit();
		}
	}, [isLoading]);

	useEffect(() => {
		if (!firstRender.current && alreadyStored.current && !isLoading && remainingTime === 0) {
			handleSubmit();
		} else {
			firstRender.current = false;
		}
	}, [remainingTime]);

	return (
		<Form className="px-5 pb-5" onSubmit={formik.handleSubmit}>
			<div className="border-secondary border-bottom">
				<div className="mb-6">
					<TagHTMLOrSkeleton text="SRPA" tag="h3" skeleton={isLoading} />
				</div>

				<div className="d-flex flex-column">
					<Col
						xs={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="patient_correctly_identified"
							labelClassName="col-md-6 me-8"
							containerClassName="flex-grow-1"
							label="Paciente corretamente identificado?"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.patient_correctly_identified}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="patient-correctly-identified-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="patient-correctly-identified-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Realizado registro de admissão após operatório em prontuário?"
							name="post_operative_admission_record"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.post_operative_admission_record}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="post-operative-admission-record-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="post-operative-admission-record-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="pacu_pain_monitoring"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Realizado monitoramento da dor?"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.pacu_pain_monitoring}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="pacu-pain-monitoring-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="pacu-pain-monitoring-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col
						xs={12}
						className="d-flex flex-responsive py-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="aldrete_scale"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Escala de Aldrete corretamente preenchida?"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.aldrete_scale}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="aldrete-scale-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="aldrete-scale-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col xs={12} className="d-flex flex-responsive py-5">
						<FormGroupRadio
							inline
							name="discharge_condition_record"
							containerClassName="flex-grow-1"
							label="Registro da condição de alta da SRPA?"
							labelClassName="col-md-6 me-8"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.discharge_condition_record}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="discharge-condition-record-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="discharge-condition-record-not-compliant"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			<div className="pt-5 border-secondary border-bottom">
				<TagHTMLOrSkeleton
					text="Posto de Internação"
					tag="h5"
					skeletonClassName="mb-4"
					skeleton={isLoading}
				/>

				<div className="d-flex flex-column">
					<Col
						xs={12}
						className="d-flex flex-responsive pb-5 border-secondary border-bottom"
					>
						<FormGroupRadio
							inline
							name="arrival_condition_record"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Registro da condição de chegada na unidade?"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.arrival_condition_record}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="arrival-condition-record-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="arrival-condition-record-not-compliant"
							/>
						</FormGroupRadio>
					</Col>

					<Col xs={12} className="d-flex flex-responsive py-5">
						<FormGroupRadio
							inline
							name="inpatient_unit_pain_monitoring"
							containerClassName="flex-grow-1"
							labelClassName="col-md-6 me-8"
							label="Realizado monitoramento da dor?"
							skeleton={isLoading}
							skeletonHeight={20}
							onChange={formik.handleChange}
							requestedValue={formik.values.inpatient_unit_pain_monitoring}
						>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.Compliant]}
								value={ConformityType.Compliant}
								id="inpatient-unit-pain-monitoring-compliant"
							/>
							<FormRadio
								label={ConformityTypeMessages[ConformityType.NotCompliant]}
								value={ConformityType.NotCompliant}
								id="inpatient-unit-pain-monitoring-not-compliant"
							/>
						</FormGroupRadio>
					</Col>
				</div>
			</div>

			{isPregnant && (
				<div className="py-5">
					<TagHTMLOrSkeleton
						text="Obstetrícia"
						tag="h5"
						skeletonClassName="mb-4"
						skeleton={isLoading}
					/>

					<div className="d-flex flex-column">
						<Col
							xs={12}
							className="d-flex flex-responsive pb-5 border-secondary border-bottom"
						>
							<FormGroupRadio
								inline
								name="arrival_binomial"
								containerClassName="flex-grow-1"
								labelClassName="col-md-6 me-8"
								label="Chegada Binômio?"
								skeleton={isLoading}
								skeletonHeight={20}
								onChange={formik.handleChange}
								requestedValue={formik.values.arrival_binomial}
							>
								<FormRadio
									label="Sim"
									value="1"
									id="arrival-binomial-compliant"
									containerClassName="width-6.5rem"
								/>

								<FormRadio
									label="Não"
									value="0"
									id="arrival-binomial-not-compliant"
								/>
							</FormGroupRadio>
						</Col>
					</div>
				</div>
			)}

			<Col xs={12} className="py-5">
				{isLoading ? (
					<SkeletonWrapper height={20} />
				) : (
					<div className="professional-select-container d-flex flex-responsive align-items-center gap-2">
						<TagHTMLOrSkeleton
							className="font-weight-bold"
							text="Profissional Responsável:"
							tag="h5"
						/>

						<ProfessionalWithRolesSelect
							classNames={{
								container: () => "col-md-4",
							}}
							menuPlacement="auto"
							roles={["Técnico de Enfermagem", "Enfermeiro"]}
							isSolid
							professionalId={formik.values.responsible_professional_id}
							onChange={(option) =>
								formik.setFieldValue(
									"responsible_professional_id",
									String((option as ReactSelectOption).value)
								)
							}
						/>
					</div>
				)}
			</Col>

			<div className="mt-20 d-flex justify-content-end">
				<SaveButton type="submit" skeleton={isLoading} />
			</div>
		</Form>
	);
}
